<template>
  <div class="status-uptime">
    <div class="status-uptime-header ds-font-weight-bold">
      {{ $labels.uptimeRate }}
    </div>
    <span
      v-if="loading"
      class="status-loading ds-margin-y-12"
      :style="{ height: '24px', width: '100%' }"
    />
    <div v-if="!loading" class="status-uptime__bar-container ds-margin-y-12">
      <template
        v-for="log in uptimeStatusDaysToDisplayByScreen"
        :key="log.date"
      >
        <StatusUptimeTooltip
          v-if="log.isDown"
          :style="tooltipStyles"
          :date="log.date"
          :description="log.formattedDowntime"
        >
          <div class="status-uptime__bar status-uptime__bar--degraded"></div>
        </StatusUptimeTooltip>
        <StatusUptimeTooltip
          v-else
          :style="tooltipStyles"
          :date="log.date"
          description="100% uptime"
        >
          <div class="status-uptime__bar status-uptime__bar--operational"></div>
        </StatusUptimeTooltip>
        <div v-if="day !== endDay" class="status-uptime__bar--space"></div>
      </template>
    </div>
    <div class="status-uptime__labels">
      <p class="ds-text-small ds-color-grey-450">{{ dayLabels.start }}</p>
      <p class="ds-text-small ds-color-grey-450">{{ dayLabels.end }}</p>
    </div>
  </div>
</template>
<script>
import { format } from "date-fns";
import { isScreenSmall, isScreenMedium } from "@/utils/screen.js";
import StatusUptimeTooltip from "./status-uptime-tooltip";
import { getUptimeStatus } from "@/utils/uptime.js";

const STATUS_BAR_ITEM_FLEX_VALUE = 7.67;

export default {
  components: {
    StatusUptimeTooltip,
  },
  inject: ["screen", "loading"],
  props: {
    startDate: { type: String, required: true },
    endDate: { type: String, required: true },
    logs: { type: Object, default: () => null },
    legends: { type: Array, required: true },
  },
  data: () => ({
    uptimeStatusPerDay: [],
    statusBarItemFlexValue: STATUS_BAR_ITEM_FLEX_VALUE,
  }),
  created() {
    this.uptimeStatusPerDay = getUptimeStatus(
      this.startDate,
      this.endDate,
      this.logs
    );
    // this.markIncidentDays(this.logs);
  },
  watch: {
    logs(newLogs) {
      this.uptimeStatusPerDay = getUptimeStatus(
        this.startDate,
        this.endDate,
        newLogs
      );
    },
  },
  computed: {
    isScreenSmall() {
      return isScreenSmall(this.screen.name);
    },
    isScreenMedium() {
      return isScreenMedium(this.screen.name);
    },
    tooltipStyles() {
      return `flex: ${this.statusBarItemFlexValue}`;
    },
    uptimeStatusDaysToDisplayByScreen() {
      if (this.isScreenSmall) {
        // Display 30 elements for Mobile
        return this.uptimeStatusPerDay.slice(-30);
      }
      if (this.isScreenMedium) {
        // Display 60 elements for Tablet
        return this.uptimeStatusPerDay.slice(-60);
      }
      return this.uptimeStatusPerDay;
    },
    dayLabels() {
      // Start day depends on the screen
      const days = this.uptimeStatusDaysToDisplayByScreen;
      const startDateByScreen = days?.[0]?.date;
      return {
        start: startDateByScreen
          ? format(new Date(startDateByScreen), "d MMMM")
          : "",
        end: this.endDate ? format(new Date(this.endDate), "d MMMM") : "",
      };
    },
    endDay() {
      return this.endDate.split("T")[0];
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~website-design-system/src/foundation/tokens/variables";
@import "website-design-system/src/foundation/mixins/mixins";

.status-uptime {
  background-color: $color-white;
  border: 1px solid $color-grey;
  display: flex;
  flex-direction: column;
  padding: $spacing-24 $spacing-12;

  @include screen("medium") {
    padding: $spacing-24;
  }

  .status-uptime-header {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }

  .status-uptime__labels {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }

  .status-uptime__bar-container {
    display: flex;
    flex-wrap: wrap;
  }

  :deep(.status-uptime__bar) {
    height: $spacing-24;

    &:hover {
      filter: brightness(75%);
    }
  }

  :deep(.status-uptime__bar--operational) {
    background-color: $color-green;
  }

  :deep(.status-uptime__bar--degraded) {
    background-color: $color-orange;
  }

  .status-uptime__bar--space {
    flex: 2;
  }
}
</style>
